'use client';

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The board row's column geometry — **one shared definition** (LF-5985 /
 * LEADS-18, `functional-spec.md` §3.4).
 *
 * The column labels ride *inside* each section's grey band (`ListSection`'s
 * `rightLabel` slot), not on a header strip of their own — the inventory Today
 * view's pattern, where `Date of Death Value` shares the band with
 * `OUTSTANDING`. That only works if the labels and the row cells are laid out
 * from the same widths, so both import `LEAD_COLUMNS` from here and neither
 * restates a class string.
 *
 * **Published to LEADS-19 (LF-5986)**, which owns the row's fifteen signals:
 * extend this map, do not write a second one. A duplicated width in the row
 * file drifts silently off the band label above it, and nothing — typecheck,
 * lint, or a DOM assertion — can see it.
 *
 * **The widths are not Tailwind's defaults.** This app scales the `--spacing`
 * knob to ~0.854x, so `w-24` renders 82px and `w-32` renders 109px. Reasoning
 * from the default scale picked a clipping width twice during the prototype —
 * measure the rendered box, never the class name.
 */
export const LEAD_COLUMNS = {
  /** `100%` at its widest, plus the dashed `Send` pill LEADS-19 adds. */
  form: 'w-20',
  /**
   * An arbitrary px value rather than a scale step, deliberately — the scale
   * jumps 82px (`w-24`) → 95px (`w-28`), and the content sits between them.
   *
   * **Measured, in Inter at 12px with `tabular-nums` at the weight each string
   * actually renders** (upcoming is `font-medium`), because character counts
   * mislead badly here: tabular digits are uniform and wide while proportional
   * letters are not, so `Today 11.30am` and `31/12 10.30am` are the same length
   * on the page and one character apart in a text editor.
   *
   * | string | width |
   * |---|---|
   * | `Today 11.30am` — the worst case | 81.2px |
   * | `31/12 10.30am` — the numeric worst case | 80.1px |
   * | the `+ Meeting` dashed pill | 70.3px |
   * | the `Meeting` band label | 42.7px |
   *
   * 86px is that worst case plus ~5px, which is the clearance a fallback font
   * needs if Inter has not loaded; the cell truncates rather than overflowing, so
   * the failure mode is an ellipsis rather than a broken row.
   *
   * **This column was 104px, on a comment claiming the worst case needed 98px.**
   * It did not; the figure was never measured and left ~23px of the row spent on
   * nothing, between the `Send` pill and a right-aligned date. Two lessons the
   * file already tried to teach and this violated anyway: measure the rendered
   * box, and re-measure when you inherit a number rather than trusting it.
   *
   * **Dropping the word `Today` would buy 1.1px** and is not worth considering —
   * the numeric worst case is immediately behind it, and `Today` marks the one
   * meeting that is happening now.
   */
  meeting: 'w-[86px]',
  /** Sized to content — the widest age is `today` at 32px. */
  age: 'w-10',
  /**
   * The solicitor, as the All Matters list's **initials bubble**.
   *
   * It is worth knowing that this column has been an avatar before. LF-6054
   * replaced a pair of overlapping `TeamAvatars` with text, on two reasons that
   * were sound then and have both since dissolved: it rendered *two* people (the
   * intake owner beside the booked solicitor) where the column asks about one,
   * and its empty state was the sentence `Not decided yet`, which no avatar can
   * render. The pairing went when the owner concept left the column, and the
   * sentence went when the empty state became the cell's own muted `—` — which
   * sits beside a bubble perfectly well. What is left is a column naming one
   * person, which is what the matter list solves with a bubble.
   *
   * **The header is gone with the names.** A column of initials is read by
   * hovering, not by scanning, so a label over 24px bubbles was chrome
   * explaining a shape that explains itself; the tooltip names the role.
   *
   * `w-10` renders ~34px on this app's ~0.854x `--spacing` scale — the bubble
   * plus breathing room, and the same width the Age column uses.
   */
  solicitor: 'w-10'
} as const;

/**
 * Gap between columns, shared by the band labels and the row.
 *
 * `gap-2` rather than `gap-3`: five gaps at ~10px each spent 51px of the row on
 * nothing, and the columns are already separated by right-alignment and by
 * being different kinds of thing.
 */
export const LEAD_COLUMN_GAP = 'gap-2';

/**
 * The flag's footprint at the left of the row (`functional-spec.md` §3.5.9).
 * Reserved by the band labels so the identity column starts level with the row
 * titles. LEADS-19 renders the control into this slot.
 */
export const LEAD_FLAG_SLOT = 'h-3.5 w-3.5 shrink-0';

/**
 * The row `⋯` menu's footprint at the right (§3.5.15). Reserved by both the
 * band labels and the row so every column shares one right edge — the trick
 * `TodayMainHeader` uses for its own actions column (LF-5334). LEADS-19
 * renders the menu into this slot.
 */
export const LEAD_MENU_SLOT = 'h-6 w-6 shrink-0';

/**
 * One right-aligned value slot on a row. `—` when there is nothing to say, so
 * the columns still read as columns.
 */
export function LeadColumnCell({
  className,
  children,
  muted = false
}: {
  /** The column's width from `LEAD_COLUMNS`. */
  className: string;
  children?: ReactNode;
  /**
   * The column has nothing to report (no form, no meeting) rather than a value
   * worth reading — added by LEADS-19, which owns the two cells that can be
   * genuinely empty.
   */
  muted?: boolean;
}) {
  return (
    <div
      className={cn(
        'text-12px flex shrink-0 items-center justify-end text-right tabular-nums',
        muted ? 'text-foreground-subtle' : 'text-muted-foreground',
        className
      )}
    >
      {children ?? '—'}
    </div>
  );
}

/**
 * A single column label inside the section band.
 *
 * Sentence case and the band's own `text-xs` rather than the uppercase
 * treatment a standalone header strip would use — the group name
 * (`NEW ENQUIRY`) is the band's uppercase run, and two competing runs in one
 * band read as a table header bolted onto a list.
 */
function ColumnLabel({
  className,
  children
}: {
  className: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn('shrink-0 text-right text-xs font-medium', className)}
      // The band's own muted colour is inherited from `ListSectionHeader`'s
      // right-label slot, so nothing here re-specifies it.
    >
      {children}
    </span>
  );
}

/**
 * The four column labels, as a node for `ListSection`'s `rightLabel` slot.
 *
 * **No `Lead` label.** The left of the band is already the section name, and a
 * `Lead` label under it would title a column whose contents are self-evidently
 * leads — on a list where every row is one.
 */
export function LeadColumnLabels() {
  return (
    <span
      className={cn('flex items-center', LEAD_COLUMN_GAP)}
      data-testid="leads-column-labels"
    >
      <ColumnLabel className={LEAD_COLUMNS.form}>Form</ColumnLabel>
      <ColumnLabel className={LEAD_COLUMNS.meeting}>Meeting</ColumnLabel>
      <ColumnLabel className={LEAD_COLUMNS.age}>Age</ColumnLabel>
      {/*
        The solicitor column carries no label — its cells are initials bubbles,
        which are read by hovering rather than by scanning, so a heading over
        them explained a shape that explains itself. The width is still reserved
        so the header row and the rows below it stay in column.

        The word, when it is needed, is `Solicitor` and never `Owner` (LF-6054):
        the product already uses *owner* for the matter owner, and a second,
        weaker one on the lead reads as the same kind of claim while meaning
        something the enquiry does not have. It now lives in the bubble's tooltip
        (`ownerLabel`).
      */}
      <span className={cn('shrink-0', LEAD_COLUMNS.solicitor)} aria-hidden />
      <span className={LEAD_MENU_SLOT} aria-hidden />
    </span>
  );
}
